import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getAccessToken, onSessionChange, refreshSession, setSession } from '@/api/client';
import { qk, useMe } from '@/api/hooks';
import type { User } from '@/api/types';
import { AuthContext, type AuthStatus, type AuthValue } from './context';

/**
 * Holds the signed-in user. On load it silently refreshes with the httpOnly cookie; if that
 * fails the status becomes `anonymous` and the route guards send the user to sign-in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>(getAccessToken() ? 'authenticated' : 'loading');
  const [sessionUser, setSessionUser] = useState<User | null>(null);

  useEffect(() => {
    const off = onSessionChange((auth) => {
      if (auth) {
        setSessionUser(auth.user);
        qc.setQueryData(qk.me, auth.user);
        setStatus('authenticated');
      } else {
        setSessionUser(null);
        setStatus('anonymous');
        qc.clear();
      }
    });
    if (!getAccessToken()) void refreshSession();
    return off;
  }, [qc]);

  const signIn = useCallback(async (email: string, password: string) => {
    const auth = await api.post('/v1/auth/login', { body: { email, password } });
    setSession(auth);
    return auth;
  }, []);

  const signUp = useCallback(async (input: { email: string; password: string; name: string; timezone?: string }) => {
    const auth = await api.post('/v1/auth/register', { body: input });
    setSession(auth);
    return auth;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/v1/auth/logout', { body: {} });
    } catch {
      /* the cookie may already be gone; sign out locally anyway */
    } finally {
      setSession(null);
    }
  }, []);

  const clear = useCallback(() => setSession(null), []);

  // The /v1/me cache is the freshest user (profile edits update it).
  const me = useMe(status === 'authenticated').data;
  const user = status === 'authenticated' ? (me ?? sessionUser) : null;

  const value = useMemo<AuthValue>(
    () => ({ status, user, signIn, signUp, signOut, clear }),
    [status, user, signIn, signUp, signOut, clear],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
